import { Directive, ElementRef, HostListener, Optional } from "@angular/core";
import { NgControl } from "@angular/forms";
import { MatDatepickerInput } from "@angular/material/datepicker";
import { Moment } from "moment";
import { parseTypedDate, TYPEABLE_DATE_DISPLAY_FORMAT, TYPEABLE_DATE_MAX_LENGTH } from "./typeable-date.parser";

const ALLOWED_CHAR = /^[0-9\-/.]$/;
const ALLOWED_STRING = /^[0-9\-/.]*$/;

@Directive({
  selector: "input[appTypeableDate][matDatepicker]",
  standalone: true,
  host: {
    maxlength: `${TYPEABLE_DATE_MAX_LENGTH}`,
    inputmode: "numeric",
    autocomplete: "off"
  }
})
export class TypeableDateDirective {
  private isRewriting = false;

  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    private readonly datepickerInput: MatDatepickerInput<Moment>,
    @Optional() private readonly ngControl: NgControl | null
  ) {}

  @HostListener("keypress", ["$event"])
  onKeypress(event: KeyboardEvent): void {
    if (event.key.length === 1 && !ALLOWED_CHAR.test(event.key)) {
      event.preventDefault();
    }
  }

  @HostListener("paste", ["$event"])
  onPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData("text") ?? "";
    if (!ALLOWED_STRING.test(text) || text.length > TYPEABLE_DATE_MAX_LENGTH) {
      event.preventDefault();
    }
  }

  @HostListener("input")
  onInput(): void {
    if (this.isRewriting) return;
    const raw = this.el.nativeElement.value;
    if (!raw) {
      this.clearTypeableError();
      return;
    }
    const digits = raw.replace(/[-/.]/g, "");
    if (digits.length !== 8) return;

    const parsed = parseTypedDate(raw);
    if (!parsed || !this.isWithinSingleBounds(parsed)) return;

    this.writeFormatted(parsed.format(TYPEABLE_DATE_DISPLAY_FORMAT), parsed);
    this.clearTypeableError();
  }

  @HostListener("blur")
  onBlur(): void {
    const input = this.el.nativeElement;
    const raw = input.value;

    if (!raw || !raw.trim()) {
      this.clearTypeableError();
      return;
    }

    const parsed = parseTypedDate(raw);
    if (!parsed || !this.isWithinSingleBounds(parsed)) {
      this.markTypeableError();
      return;
    }

    this.clearTypeableError();
    const formatted = parsed.format(TYPEABLE_DATE_DISPLAY_FORMAT);
    this.writeFormatted(formatted, parsed);
  }

  private isWithinSingleBounds(parsed: Moment): boolean {
    const min = this.datepickerInput.min;
    const max = this.datepickerInput.max;
    if (min && parsed.isBefore(min, "day")) return false;
    if (max && parsed.isAfter(max, "day")) return false;
    return true;
  }

  private writeFormatted(value: string, parsed: Moment): void {
    const input = this.el.nativeElement;
    this.isRewriting = true;
    if (input.value !== value) {
      input.value = value;
    }
    try {
      input.setSelectionRange(value.length, value.length);
    } catch {
      // some input types disallow selection range; ignore.
    }
    this.isRewriting = false;
    this.commitToDatepicker(parsed);
  }

  // Drives Material's internal selection model directly so the typed date is
  // treated exactly like a calendar pick (CVA propagation, (dateChange)/(dateInput)
  // emission, calendar highlight). The public MatDatepickerInput.value setter
  // guards against self-sourced re-emits and won't push to ngModel, so we go
  // through _model.updateSelection with `this` as a non-self source. Reaches
  // into a Material private; revisit on Material upgrades.
  private commitToDatepicker(parsed: Moment): void {
    const model = (this.datepickerInput as unknown as { _model: { updateSelection: (v: unknown, source: unknown) => void } })._model;
    model.updateSelection(parsed.clone(), this);
    const picker = (this.datepickerInput as unknown as { _datepicker?: { opened: boolean; close: () => void } })._datepicker;
    if (picker?.opened) {
      picker.close();
    }
  }

  private markTypeableError(): void {
    const control = this.ngControl?.control;
    if (!control) return;
    const existing = control.errors ?? {};
    control.setErrors({ ...existing, typeableDate: true });
    control.markAsTouched();
  }

  private clearTypeableError(): void {
    const control = this.ngControl?.control;
    if (!control || !control.errors) return;
    const { typeableDate, ...rest } = control.errors;
    control.setErrors(Object.keys(rest).length ? rest : null);
  }
}
